import { createCreditCheckoutSession } from '@/api/payment';
import {
  prepareGoogleAdsBeginCheckoutTag,
  requestGoogleAdsBeginCheckoutRetry,
  trackGoogleAdsBeginCheckoutBeforeNavigation,
} from '@/analytics/google-ads-begin-checkout';
import {
  classifyCheckoutFailure,
  trackRetentionEvent,
} from '@/analytics/retention-events';
import { Button } from '@/components/ui/button';
import { m } from '@/locale/paraglide/messages';
import { clearCheckoutReturnOfferMarker } from '@/lib/checkout-return-offer';
import { cn } from '@/lib/utils';
import { IconLoader2 } from '@tabler/icons-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface CreditCheckoutButtonProps {
  packageId: string;
  returnUrl?: string;
  /** Extra metadata to attach to the checkout session. */
  metadata?: Record<string, string>;
  returnTo?: string;
  onBeforeCheckout?: () => void | Promise<void>;
  variant?:
    | 'default'
    | 'outline'
    | 'destructive'
    | 'secondary'
    | 'ghost'
    | 'link'
    | null;
  size?: 'default' | 'sm' | 'lg' | 'icon' | null;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

/** Checkout button for one-time credit-package purchases. */
export function CreditCheckoutButton({
  packageId,
  returnUrl,
  metadata,
  returnTo,
  onBeforeCheckout,
  variant = 'default',
  size = 'default',
  className,
  disabled,
  children,
}: CreditCheckoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    try {
      setIsLoading(true);
      prepareGoogleAdsBeginCheckoutTag();
      await onBeforeCheckout?.();

      const mergedMetadata: Record<string, string> = metadata
        ? { ...metadata }
        : {};

      const result = await createCreditCheckoutSession({
        data: {
          packageId,
          returnTo,
          returnUrl: new URL(
            returnUrl ?? window.location.href,
            window.location.origin
          ).toString(),
          metadata:
            Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined,
        },
      });

      if (result?.url) {
        trackRetentionEvent('begin_checkout', {
          currency: result.beginCheckout.currency,
          packageId,
          paymentScene: 'credit',
          source: metadata?.source ?? 'credits_page',
          value: result.beginCheckout.value,
        });
        clearCheckoutReturnOfferMarker();
        try {
          await trackGoogleAdsBeginCheckoutBeforeNavigation(
            result.beginCheckout
          );
        } catch (error) {
          console.warn('Begin-checkout tracking failed:', error);
        } finally {
          window.location.href = result.url;
        }
        return;
      } else {
        trackRetentionEvent('checkout_start_failed', {
          errorCategory: 'invalid_response',
          packageId,
          paymentScene: 'credit',
          source: metadata?.source ?? 'credits_page',
        });
        toast.error(m.settings_credits_checkout_failed());
      }
    } catch (err) {
      trackRetentionEvent('checkout_start_failed', {
        errorCategory: classifyCheckoutFailure(err),
        packageId,
        paymentScene: 'credit',
        source: metadata?.source ?? 'credits_page',
      });
      console.error('Credit checkout error:', err);
      requestGoogleAdsBeginCheckoutRetry();
      toast.error(m.settings_credits_checkout_failed());
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={cn(className)}
      onClick={handleClick}
      onFocus={prepareGoogleAdsBeginCheckoutTag}
      onPointerDown={prepareGoogleAdsBeginCheckoutTag}
      onPointerEnter={prepareGoogleAdsBeginCheckoutTag}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <>
          <IconLoader2 className="mr-2 size-4 animate-spin" />
          {m.common_loading()}
        </>
      ) : (
        children
      )}
    </Button>
  );
}
